import { Locale } from '../ruleset/locale';

export const MINI_APP_REDIRECT_URL = `https://miniapp.line.me/${process.env.LINE_MINI_APP_LIFF_ID ?? '2007326019-vAg8J6Bp'}`;

const VIRTUAL_ENV_VERSION_QUERY_PARAM = 'buildVersion';

export type AlternativeOpenWebBuckets =
  | 'control'
  | 'enabled_iab' // It should be enabled_web, but we can't change it, because this used in analytics
  | 'enabled_web'
  | 'enabled_mini_app'
  | 'native_crossplay_open_web';

/**
 * Get app link - always returns MiniApp URL
 *
 * Since LIFF → MiniApp migration is complete, all chatbot messages
 * should point to the MiniApp. This provides a safety net ensuring
 * no LIFF URLs are ever generated, regardless of any stale data.
 */
export function getLineRedirectURL(payload: object) {
  return getMiniAppLink(payload);
}

export function getMiniAppLink(payload: object): string {
  let url = new URL(MINI_APP_REDIRECT_URL);

  const payloadStr = payload ? JSON.stringify(payload) : '';
  if (payloadStr) {
    url.searchParams.set('payload', payloadStr);
  }

  const virtualEnvVersion =
    process.env.IS_DEVELOPMENT &&
    process.env.STAGE !== 'dev' &&
    getVirtualEnvVersion();
  if (virtualEnvVersion) {
    url.searchParams.set(VIRTUAL_ENV_VERSION_QUERY_PARAM, virtualEnvVersion);
  }

  return url.toString();
}

export function getOpenWebAppLink(payload: object): string {
  let url = new URL(getBaseURL());

  const payloadStr = payload ? JSON.stringify(payload) : '';
  if (payloadStr) {
    url.searchParams.set('payload', payloadStr);
  }

  // Force open in browser instead of native IOS app
  url.searchParams.append('openInApp', '0');

  // Open in external browser
  url.searchParams.append('openExternalBrowser', '1');

  return url.toString();
}

/**
 * Open Open Web App in LINE browser
 *
 * @param payload
 * @returns
 */
export function getIABLink(payload: object): string {
  let url = new URL(getBaseURL());

  const payloadStr = payload ? JSON.stringify(payload) : '';
  if (payloadStr) {
    url.searchParams.set('payload', payloadStr);
  }

  // Force open in browser instead of native IOS app
  url.searchParams.append('openInApp', '0');

  // Open in internal line browser app
  url.searchParams.append('openExternalBrowser', '0');

  return url.toString();
}

export function getBaseURL() {
  const stage = process.env.STAGE;

  switch (stage) {
    case 'alpha':
    case 'bravo':
    case 'charlie':
    case 'delta':
    case 'echo':
    case 'foxtrot': {
      const virtualEnvVersion = getVirtualEnvVersion();
      if (virtualEnvVersion)
        return `https://buruburu-line-alpha.cdn.gc-internal.net/index.html?${VIRTUAL_ENV_VERSION_QUERY_PARAM}=${virtualEnvVersion}`;

      return 'https://buruburu-line-alpha.cdn.gc-internal.net/index.html';
    }

    case 'qa':
      return 'https://buruburu-line-qa.cdn.gc-internal.net/index.html';

    case 'dev':
      return 'https://buruburu-line-dev.cdn.gc-internal.net/index.html';

    case 'prod':
    default:
      return 'https://buruburu.app/index.html';
  }
}

export function getBaseMiniAppURL() {
  const stage = process.env.STAGE;

  switch (stage) {
    case 'alpha':
    case 'bravo':
    case 'charlie':
    case 'delta':
    case 'echo':
    case 'foxtrot': {
      const virtualEnvVersion = getVirtualEnvVersion();
      if (virtualEnvVersion)
        return `https://buruburu-line-miniapp-alpha.cdn.gc-internal.net/index.html?${VIRTUAL_ENV_VERSION_QUERY_PARAM}=${virtualEnvVersion}`;

      return 'https://buruburu-line-miniapp-alpha.cdn.gc-internal.net/index.html';
    }

    case 'dev':
      return 'https://miniapp.line.me/2007326016-zOX8NqQB';

    case 'qa':
      return 'https://miniapp.line.me/2007326017-mBynPGK4';

    case 'prod':
    default:
      return 'https://miniapp.line.me/2007326019-vAg8J6Bp';
  }
}

export function getOpenWebGameURL(
  payload: object,
  args: {
    locale: Locale;
    demoPayload?: object;
  },
) {
  const { locale } = args;
  const baseURL = getBaseURL();

  const url = new URL(baseURL);
  url.searchParams.append('openExternalBrowser', '1');
  url.searchParams.append('openInApp', '0');
  // additional payment intent param for customized splash screen
  url.searchParams.append('payment-intent', '1');
  url.searchParams.append('hl', locale);

  if (payload) {
    const payloadStr = JSON.stringify(payload);
    url.searchParams.append('payload', payloadStr);
  }

  if (args.demoPayload) {
    const payloadStr = JSON.stringify(args.demoPayload);
    url.searchParams.append('demoPayload', payloadStr);
  }

  return url.toString();
}

function getVirtualEnvVersion(): string | null {
  if (typeof location === 'undefined') {
    return null;
  }

  return new URL(location.href).searchParams.get(
    VIRTUAL_ENV_VERSION_QUERY_PARAM,
  );
}
